import * as Api from '@ViewModels';
import {
	ActionItemViewModel,
	EntityViewModel,
	IEntity,
	IRichContentEditorState,
	IUser,
	RichContentComposerResult,
	RichContentReferenceMethod,
	VmUtils,
} from '@ViewModels';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { ResourceVisibility } from '../../VisibilityDropdown';
import { buildActionItemToSave } from './buildActionItemToSave';

interface IOptions {
	onSaved?: (vm: ActionItemViewModel, result: RichContentComposerResult) => void;
	onDeleted?: (vm: ActionItemViewModel) => void;
}

/**
 * Headless controller for editing an {@link ActionItemViewModel}: owns the editable form state,
 * re-derives it whenever the target item changes, and exposes `save`/`remove` that run the shared
 * {@link buildActionItemToSave} mapping through the VM and fire the same event logging + local
 * notifications the legacy modal composer did (so subscribed lists/timelines still refresh). All
 * chrome (headers, footers, layout) is left to the consuming component.
 *
 * The consumer should be wrapped in `observer()` so the VM's `isBusy`/`isSaving` stay reactive.
 */
export function useActionItemEditor(actionItem: ActionItemViewModel | null, options?: IOptions) {
	const userSession = useUserSession();
	const { logEvent, logApiError } = useEventLogging('ActionItemEditor');
	const { postNotification } = useLocalNotificationService<Api.IActionItem>();
	const errorMessages = useErrorMessages();

	const [assignee, setAssignee] = React.useState<IUser | null>(null);
	const [checked, setChecked] = React.useState(false);
	const [dueDate, setDueDate] = React.useState<Date | null>(null);
	const [hasDueTime, setHasDueTime] = React.useState(false);
	const [priority, setPriority] = React.useState<Api.ActionItemPriority>('None');
	const [editorState, setEditorState] = React.useState<IRichContentEditorState | null>(null);
	const [mentionedEntities, setMentionedEntities] = React.useState<EntityViewModel<IEntity>[]>([]);
	const [visibility, setVisibility] = React.useState<ResourceVisibility>('admin');
	const [inputErrorMessage, setInputErrorMessage] = React.useState<string | null>(null);

	// Re-derive the form whenever the target item changes identity (mirrors the class composer's
	// getNextStateWithProps). Keyed on the VM instance, which the caller creates once per edit.
	React.useEffect(() => {
		if (!actionItem) {
			return;
		}
		setAssignee(actionItem.assignee ?? null);
		setChecked(!!actionItem.isCompleted);
		setDueDate(actionItem.dueDate ?? null);
		const model = actionItem.toActionItem();
		setHasDueTime(!!actionItem.dueDate && !!model.hasDueTime);
		setPriority(model.priority ?? 'None');
		setMentionedEntities(
			(actionItem.mentionedEntities || [])
				.filter(reference => reference.method === RichContentReferenceMethod.Explicit)
				.map(reference => reference.entity)
		);
		setEditorState(convertRawRichTextContentStateToRichContentEditorState(actionItem.rawContentState));
		setVisibility(actionItem.id ? actionItem.visibility : VmUtils.getDefaultVisibility(userSession.user));
		setInputErrorMessage(null);
	}, [actionItem, userSession]);

	const onEditorStateChanged = React.useCallback((content: IRichContentEditorState) => {
		setEditorState(content);
		setInputErrorMessage(null);
	}, []);

	const clearError = React.useCallback(() => setInputErrorMessage(null), []);

	const save = React.useCallback(async (): Promise<boolean> => {
		if (!actionItem) {
			return false;
		}
		if (!editorState || !editorState.hasContent()) {
			setInputErrorMessage('A description is required');
			return false;
		}
		const isEdit = !!actionItem.id;
		const model = buildActionItemToSave(actionItem, {
			editorState,
			dueDate,
			hasDueTime,
			priority,
			assignee,
			checked,
			visibility,
			mentionedEntities,
		});
		const promise = actionItem.save(model);
		if (!promise) {
			return false;
		}
		const action = isEdit ? 'EditActionItem' : 'SaveActionItem';
		logEvent(action, { id: model.id });
		try {
			const saved = await promise;
			postNotification({ info: saved, topic: isEdit ? Topics.EDIT_ACTION_ITEM : Topics.CREATE_ACTION_ITEM });
			options?.onSaved?.(actionItem, isEdit ? RichContentComposerResult.Edit : RichContentComposerResult.Create);
			return true;
		} catch (error) {
			logApiError(`${action}-Error`, error as Api.IOperationResultNoValue);
			setInputErrorMessage((error as Api.IOperationResultNoValue)?.systemMessage ?? 'Failed to save action item');
			return false;
		}
	}, [
		actionItem,
		editorState,
		dueDate,
		hasDueTime,
		priority,
		assignee,
		checked,
		visibility,
		mentionedEntities,
		logEvent,
		logApiError,
		postNotification,
		options,
	]);

	const remove = React.useCallback(async (): Promise<void> => {
		if (!actionItem) {
			return;
		}
		const promise = actionItem.delete();
		if (!promise) {
			return;
		}
		logEvent('DeleteActionItem', { id: actionItem.id });
		try {
			await promise;
			postNotification({ info: actionItem.toActionItem(), topic: Topics.DELETE_ACTION_ITEM });
			options?.onDeleted?.(actionItem);
		} catch (error) {
			logApiError('DeleteActionItem-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error as Api.IOperationResultNoValue);
		}
	}, [actionItem, logEvent, logApiError, postNotification, errorMessages, options]);

	return {
		assignee,
		checked,
		dueDate,
		hasDueTime,
		priority,
		editorState,
		mentionedEntities,
		visibility,
		inputErrorMessage,
		setAssignee,
		setChecked,
		setDueDate,
		setHasDueTime,
		setPriority,
		setMentionedEntities,
		setVisibility,
		onEditorStateChanged,
		clearError,
		save,
		remove,
	};
}

export type ActionItemEditorController = ReturnType<typeof useActionItemEditor>;
